import type { UseCaseFormData } from "../../types/use-case.types"

export type TemplateInfo = {
  id: string
  name: string
  painFocus: string
  solutionFocus: string
  data: UseCaseFormData
}

const baseDefaults: Pick<
  UseCaseFormData,
  | "owner"
  | "salary"
  | "directYear"
  | "investment"
  | "opCostUnit"
  | "implTime"
  | "comments"
  | "statIdx"
  | "togRD"
> = {
  owner: "",
  salary: "70000",
  directYear: "0",
  investment: "0",
  opCostUnit: "monthly",
  implTime: "1-3 months",
  comments: "",
  statIdx: 2,
  togRD: "no",
}

export const TEMPLATES: TemplateInfo[] = [
  {
    id: "auto-reporting",
    name: "Automate Manual Reporting",
    painFocus: "Hours on spreadsheets",
    solutionFocus: "Auto-generated dashboards",
    data: {
      ...baseDefaults,
      ucName: "Automate Manual Reporting",
      issue:
        "Teams spend 10+ hours per week compiling data from multiple sources into Excel reports. The process is error-prone, delays decision-making, and scales poorly as the business grows.",
      solutionDesc:
        "Deploy an automated reporting pipeline that connects to source systems (ERP, CRM, production databases), aggregates data in real time, and publishes interactive dashboards accessible via web and mobile.",
      dataNeeded: "ERP system exports, CRM data, production databases, existing Excel templates",
      timeSavedHours: "8",
      timeSavedUnit: "week",
      users: "15",
      opCost: "500",
      feasV: "3",
      bpSel: ["Speed", "Cost"],
      saSel: ["OE"],
      efSel: ["Accounting & Finance"],
      siteAreaSel: [],
      togTS: "yes",
      togQI: "yes",
      togWW: "yes",
    },
  },
  {
    id: "ai-quality-inspection",
    name: "AI Quality Inspection",
    painFocus: "Manual QC, human error",
    solutionFocus: "Computer vision + ML",
    data: {
      ...baseDefaults,
      ucName: "AI-Powered Quality Inspection",
      issue:
        "Manual quality checks on the production line are slow, inconsistent, and miss subtle defects. Defect escape rates are 2-5%, leading to costly rework and customer complaints.",
      solutionDesc:
        "Implement computer vision cameras at critical inspection points. An ML model trained on historical defect images classifies products in real time, flagging defects instantly and reducing escape rate below 0.5%.",
      dataNeeded:
        "Historical defect images, production line camera feeds, defect classification taxonomy",
      timeSavedHours: "4",
      timeSavedUnit: "week",
      users: "20",
      opCost: "1200",
      feasV: "2",
      bpSel: ["Quality", "Speed"],
      saSel: ["OE"],
      efSel: ["Production & Mfg"],
      siteAreaSel: [],
      togTS: "yes",
      togQI: "yes",
      togWW: "yes",
    },
  },
  {
    id: "smart-customer-support",
    name: "Smart Customer Support",
    painFocus: "Slow response, scaling",
    solutionFocus: "AI chatbot + knowledge base",
    data: {
      ...baseDefaults,
      ucName: "Smart Customer Support Chatbot",
      issue:
        "Customer support handles 500+ tickets/week. Average response time is 4 hours, satisfaction scores are declining, and hiring more agents is not sustainable.",
      solutionDesc:
        "Deploy an AI-powered chatbot with RAG (retrieval-augmented generation) over the internal knowledge base. The bot handles tier-1 queries instantly, escalates complex cases to agents with full context, and learns from resolved tickets.",
      dataNeeded:
        "Knowledge base articles, historical support tickets, product documentation, FAQ database",
      timeSavedHours: "6",
      timeSavedUnit: "week",
      users: "8",
      opCost: "800",
      feasV: "3",
      bpSel: ["Speed", "Scalability"],
      saSel: ["CE"],
      efSel: ["Customer Service"],
      siteAreaSel: [],
      togTS: "yes",
      togQI: "yes",
      togWW: "yes",
    },
  },
  {
    id: "predictive-maintenance",
    name: "Predictive Maintenance",
    painFocus: "Unplanned downtime",
    solutionFocus: "Sensor data + ML predictions",
    data: {
      ...baseDefaults,
      ucName: "Predictive Maintenance System",
      issue:
        "Unplanned equipment failures cause 20+ hours of downtime per month, disrupting production schedules and incurring emergency repair costs 3x higher than planned maintenance.",
      solutionDesc:
        "Install IoT sensors on critical machinery to capture vibration, temperature, and pressure data. An ML model predicts failures 2-4 weeks in advance, enabling planned maintenance windows and reducing unplanned downtime by 80%.",
      dataNeeded:
        "IoT sensor data streams, maintenance history logs, equipment manuals, spare parts inventory",
      timeSavedHours: "12",
      timeSavedUnit: "month",
      users: "10",
      opCost: "1500",
      feasV: "2",
      bpSel: ["Cost", "Quality"],
      saSel: ["OE"],
      efSel: ["Production & Mfg", "Technology & Projects"],
      siteAreaSel: [],
      togTS: "yes",
      togQI: "yes",
      togWW: "no",
    },
  },
  {
    id: "document-processing",
    name: "Document Processing",
    painFocus: "Manual data entry",
    solutionFocus: "OCR + NLP extraction",
    data: {
      ...baseDefaults,
      ucName: "Intelligent Document Processing",
      issue:
        "Staff manually extract data from invoices, contracts, and purchase orders — processing 200+ documents/week. Error rates average 3-5%, causing downstream accounting mismatches and delayed payments.",
      solutionDesc:
        "Implement OCR + NLP pipeline to automatically extract structured data from scanned and digital documents. The system validates extracted fields against business rules and routes exceptions to human reviewers.",
      dataNeeded:
        "Sample documents (invoices, POs, contracts), field mapping rules, ERP integration API",
      timeSavedHours: "10",
      timeSavedUnit: "week",
      users: "6",
      opCost: "600",
      feasV: "3",
      bpSel: ["Speed", "Quality", "Cost"],
      saSel: ["OE"],
      efSel: ["Accounting & Finance", "Purchasing"],
      siteAreaSel: [],
      togTS: "yes",
      togQI: "yes",
      togWW: "yes",
    },
  },
  {
    id: "process-optimization",
    name: "Process Optimization",
    painFocus: "Bottlenecks, waste",
    solutionFocus: "Process mining + AI recommendations",
    data: {
      ...baseDefaults,
      ucName: "AI-Driven Process Optimization",
      issue:
        "Key business processes have hidden bottlenecks and unnecessary steps that waste 15-20% of cycle time. No systematic way to identify and quantify inefficiencies across departments.",
      solutionDesc:
        "Deploy process mining on event logs from ERP/MES systems. AI analyzes execution patterns, identifies bottlenecks and deviations, and recommends specific improvements with estimated impact. Continuous monitoring tracks optimization progress.",
      dataNeeded: "ERP event logs, MES system data, process documentation, SOP definitions",
      timeSavedHours: "6",
      timeSavedUnit: "month",
      users: "12",
      opCost: "1000",
      feasV: "2",
      bpSel: ["Bottleneck", "Cost", "Speed"],
      saSel: ["OE"],
      efSel: ["Technology & Projects"],
      siteAreaSel: [],
      togTS: "yes",
      togQI: "no",
      togWW: "yes",
    },
  },
  {
    id: "compliance-automation",
    name: "Compliance Automation",
    painFocus: "Manual audit, risk",
    solutionFocus: "Automated monitoring + alerts",
    data: {
      ...baseDefaults,
      ucName: "Compliance Monitoring Automation",
      issue:
        "Compliance teams spend 40% of their time on manual audit tasks — checking documents, verifying certifications, and tracking regulatory changes. Gaps in coverage create risk exposure.",
      solutionDesc:
        "Automate compliance monitoring with AI that continuously scans regulatory feeds, verifies document validity, tracks certification expiry, and generates audit-ready reports. Alert system notifies teams of upcoming deadlines and non-conformances.",
      dataNeeded:
        "Regulatory databases, certification records, audit history, internal policy documents",
      timeSavedHours: "5",
      timeSavedUnit: "week",
      users: "4",
      opCost: "700",
      feasV: "2",
      bpSel: ["Compliance", "Quality"],
      saSel: ["OE"],
      efSel: ["Law & Regulatory"],
      siteAreaSel: [],
      togTS: "yes",
      togQI: "yes",
      togWW: "no",
    },
  },
  {
    id: "knowledge-management",
    name: "Knowledge Management",
    painFocus: "Tribal knowledge loss",
    solutionFocus: "AI-powered search + docs",
    data: {
      ...baseDefaults,
      ucName: "AI Knowledge Management Hub",
      issue:
        "Critical operational knowledge lives in the heads of senior staff, scattered SharePoint sites, and outdated wikis. New employees take months to become productive. When experts leave, knowledge is lost permanently.",
      solutionDesc:
        "Build an AI-powered knowledge hub that ingests documents from SharePoint, wikis, email archives, and chat history. Semantic search enables natural-language queries. The system auto-generates summaries, identifies knowledge gaps, and suggests documentation updates.",
      dataNeeded:
        "SharePoint libraries, wiki content, email archives (anonymized), training materials, SOPs",
      timeSavedHours: "3",
      timeSavedUnit: "week",
      users: "50",
      opCost: "900",
      feasV: "3",
      bpSel: ["Speed", "Scalability"],
      saSel: ["OE", "CE"],
      efSel: ["Generally Applicable"],
      siteAreaSel: [],
      togTS: "yes",
      togQI: "no",
      togWW: "yes",
    },
  },
]
